import { AlertTriangle, Check, ChevronsUpDown, X } from "lucide-react";
import { useMemo, useState } from "react";

import {
  AdminConsentBadge,
  PrivilegedBadge,
  RiskBadge,
} from "@/components/app-registration-badges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAppRegistrationScopes } from "@/lib/api";
import {
  type DelegatedScope,
  groupScopes,
  indexScopes,
  searchScopes,
} from "@/lib/app-registration-scopes";
import { cn } from "@/lib/utils";

/**
 * The most search results the list renders at once. The catalogue is every
 * Graph delegated permission (~865); rendering a broad match ("read") whole
 * makes every keystroke slow, and nobody scrolls 400 rows — they type more.
 */
const MAX_RESULTS = 50;

/**
 * Pick delegated permissions out of the server's catalogue.
 *
 * Lifted out of the intake dialog when amending an issued registration became
 * possible: a requester editing permissions is doing exactly what a requester
 * asking for them does, from the same catalogue, with the same risk and
 * admin-consent badges and the same cap. A second picker would have been the
 * one screen where `Mail.Read` could read differently.
 *
 * Presentation only -- it holds no request state and knows nothing about
 * amendments. The caller owns `selected`, which is what lets the amend form
 * seed it with what the registration already holds and diff against that.
 *
 * The catalogue is every Microsoft Graph delegated permission, so the list
 * opens on the common ones (the hand-described few most apps need), grouped as
 * before, and a search reaches all of them -- by name, Microsoft's display
 * name or the description, at most `MAX_RESULTS` shown. Results stay in one
 * list in rank order (`searchScopes`, the server's ranking: the name match
 * first, common scopes first among equals), with common rows marked "Common"
 * (only there: the opening list is all common), because
 * cmdk highlights the first row and Enter picks it: typing `People.Read` must
 * land on `People.Read`, not on a common scope that merely mentions people.
 * Filtering is ours, not cmdk's fuzzy scorer, for the same reason.
 */
export function ScopePicker({
  selected,
  onToggle,
  disabled,
  atLimit,
  excluded,
}: {
  selected: string[];
  onToggle: (key: string) => void;
  disabled: boolean;
  atLimit: boolean;
  /** Catalogue keys not to offer at all, e.g. scopes the server always keeps. */
  excluded?: readonly string[];
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const { data: scopes, isLoading, error } = useAppRegistrationScopes();
  const offered = useMemo(() => {
    const hidden = new Set(excluded ?? []);
    return (scopes ?? []).filter((scope) => !hidden.has(scope.key));
  }, [scopes, excluded]);
  // Grouped for display only — the sections and the scopes inside them keep
  // the server's picker order, which runs least dangerous first. A group left
  // empty by `excluded` is dropped with it.
  const commonGroups = useMemo(
    () => groupScopes(offered.filter((scope) => scope.common)),
    [offered],
  );
  const matches = useMemo(() => searchScopes(offered, query), [offered, query]);
  const byKey = useMemo(() => indexScopes(scopes), [scopes]);
  const searching = query.trim().length > 0;
  const shown = matches.slice(0, MAX_RESULTS);

  const option = (scope: DelegatedScope) => {
    const isSelected = selectedSet.has(scope.key);
    return (
      <ScopeOption
        key={scope.key}
        scope={scope}
        selected={isSelected}
        // Only search results mix common and other scopes; the opening list
        // is all common, so a badge on every row there would say nothing.
        markCommon={searching}
        // At the cap, only already-selected rows stay actionable, so the
        // limit reads as "unselect something" rather than a submit-time
        // rejection.
        blocked={atLimit && !isSelected}
        onToggle={onToggle}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="text-muted-foreground rounded-lg border p-3 text-sm">
        Loading the permission catalogue…
      </div>
    );
  }

  // A picker with no catalogue is not a picker with nothing to offer — it is a
  // picker that cannot be trusted, so say so instead of rendering an empty box
  // that reads as "no permissions are available".
  if (error || offered.length === 0) {
    return (
      <p className="text-destructive flex items-start gap-2 rounded-lg border p-3 text-sm">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
        <span>
          Couldn&apos;t load the permission catalogue, so there is nothing to pick from. Close the
          dialog and reopen it to retry.
        </span>
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          // Each opening starts from the common permissions again.
          if (!next) {
            setQuery("");
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id="app-reg-scopes"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className={selected.length === 0 ? "text-muted-foreground" : undefined}>
              {selected.length === 0
                ? "Choose delegated permissions…"
                : `${selected.length} permission${selected.length === 1 ? "" : "s"} selected`}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {/* Width is pinned to the trigger so the list can't overflow the
            dialog, and the scroll lives on the list rather than the popover
            so the search box stays put while the catalogue scrolls. */}
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Search all ${offered.length} permissions…`}
              value={query}
              onValueChange={setQuery}
            />
            <CommandList className="max-h-80">
              <CommandEmpty>
                No permission matches that. Try another word, or the exact name (e.g.
                Sites.Selected).
              </CommandEmpty>
              {searching ? (
                <>
                  {shown.length > 0 && (
                    <CommandGroup
                      heading={`${matches.length} match${matches.length === 1 ? "" : "es"}`}
                    >
                      {shown.map(option)}
                    </CommandGroup>
                  )}
                  {matches.length > shown.length && (
                    <p className="text-muted-foreground px-3 py-2 text-xs">
                      Showing the best {shown.length} of {matches.length} matches — keep typing to
                      narrow it.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-muted-foreground px-3 pt-2 text-xs">
                    Commonly requested permissions. Type to search all {offered.length}.
                  </p>
                  {commonGroups.map(({ group, scopes: groupScopeList }) => (
                    <CommandGroup key={group} heading={group}>
                      {groupScopeList.map(option)}
                    </CommandGroup>
                  ))}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* The selection stays visible with the menu closed — a count alone
          would hide what is actually being asked for, which is the one thing
          the approver will judge. */}
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {selected.map((key) => (
            <li key={key}>
              <button
                type="button"
                onClick={() => onToggle(key)}
                disabled={disabled}
                aria-label={`Remove ${byKey.get(key)?.value ?? key}`}
                className="bg-secondary text-secondary-foreground hover:bg-secondary/70 inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono text-xs disabled:opacity-50"
              >
                {byKey.get(key)?.value ?? key}
                <X className="size-3" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * One permission in the list: its name, privileged, risk and admin-consent
 * badges, and what it allows — plus "Common" when `markCommon` (search
 * results). Microsoft's descriptions can run to a paragraph, so they are
 * clamped, with the whole text on hover.
 */
function ScopeOption({
  scope,
  selected,
  blocked,
  markCommon,
  onToggle,
}: {
  scope: DelegatedScope;
  selected: boolean;
  blocked: boolean;
  markCommon: boolean;
  onToggle: (key: string) => void;
}) {
  return (
    <CommandItem
      value={scope.key}
      disabled={blocked}
      onSelect={() => onToggle(scope.key)}
      className="items-start gap-3"
    >
      <Check className={cn("mt-0.5 size-4 shrink-0", selected ? "opacity-100" : "opacity-0")} />
      <div className="min-w-0 space-y-0.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-sm">{scope.value}</span>
          {scope.privileged && <PrivilegedBadge />}
          <RiskBadge risk={scope.risk} />
          {scope.admin_consent_required && <AdminConsentBadge />}
          {markCommon && scope.common && (
            <Badge variant="ghost" className="text-muted-foreground border-border">
              Common
            </Badge>
          )}
        </div>
        <p
          className="text-muted-foreground line-clamp-3 text-xs whitespace-normal"
          title={scope.description}
        >
          {scope.description}
        </p>
      </div>
    </CommandItem>
  );
}
